import { useState, useEffect } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  where,
  getDocs,
  addDoc,
  getDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { db, auth } from "../../firebase";

export const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [emergencyNotification, setEmergencyNotification] = useState(null);
  const [showEmergencyPopup, setShowEmergencyPopup] = useState(false);

  useEffect(() => {
    let notificationUnsubscribe;

    const setupRealtimeListeners = async () => {
      if (!auth.currentUser) return;

      try {
        const userDoc = await getDoc(
          doc(db, "users", sessionStorage.getItem("userId"))
        );
        const userData = userDoc.data();

        const messagesQuery = query(
          collection(db, "message-center"),
          where("active", "==", true),
          where("global_notification", "==", true)
        );

        notificationUnsubscribe = onSnapshot(messagesQuery, (snapshot) => {
          const notificationData = snapshot.docs
            .map((doc) => ({
              id: doc.id,
              ...doc.data(),
            }))
            .filter((message) => {
              if (message.expirationDate) {
                const now = new Date().getTime();
                const expirationTime = message.expirationDate.seconds * 1000;
                if (now > expirationTime) {
                  return false; // Message has expired
                }
              }
              if (message.type === "county_message") {
                return message.recipients.counties.includes(userData.county);
              } else if (message.type === "disaster_message") {
                return (
                  Array.isArray(message.disaster_county) &&
                  message.disaster_county.includes(userData.county)
                );
              } else if (message.type === "partner_message") {
                return message.targetedPartners.includes(
                  sessionStorage.getItem("userId")
                );
              } else if (
                message.type === "chat_message" ||
                message.type === "access_account_message" ||
                message.type === "access_lockbox_message"
              ) {
                const userId = sessionStorage.getItem("userId");
                // First check if the user is in the recipients list
                const isRecipient = message.chat_recipients.includes(userId);
                // Then check if the message is unread for this user`
                const isUnread =
                  (message.read_by && message.read_by[userId] === false) ||
                  (message.read_by === undefined && !message.read);

                // Only return true if the user is a recipient AND the message is unread
                return isRecipient && isUnread;
              }
              return false;
            });

          setNotifications(notificationData);

          const userId = sessionStorage.getItem("userId");
          const unreadCount = notificationData.filter((notification) => {
            // Check if this notification has a read_by field and if the current user has read it
            if (
              notification.read_by &&
              notification.read_by[userId] === false
            ) {
              return true;
            }
            // For backward compatibility with old notifications that use the read field
            if (notification.read_by === undefined && !notification.read) {
              return true;
            }
            return false;
          }).length;
          setUnreadNotifications(unreadCount);

          const activeEmergencies = notificationData.filter(
            (notification) =>
              notification.urgency === "emergency" && notification.active
          );
          setEmergencyNotification(activeEmergencies);

          // if (activeEmergency && !activeEmergency.read) {
          //     setShowEmergencyPopup(true);
          // }
        });
      } catch (error) {
        console.error("Error in notifications setup:", error);
      }
    };

    setupRealtimeListeners();

    const authUnsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setupRealtimeListeners();
      } else {
        setNotifications([]);
        setUnreadNotifications(0);
        setEmergencyNotification(null);
        setShowEmergencyPopup(false);
      }
    });

    return () => {
      if (notificationUnsubscribe) notificationUnsubscribe();
      if (authUnsubscribe) authUnsubscribe();
    };
  }, []);

  const markNotificationAsRead = async (notificationId) => {
    if (!auth.currentUser) return;
    const userId = sessionStorage.getItem("userId");

    try {
      const notificationRef = doc(db, "message-center", notificationId);
      const notificationDoc = await getDoc(notificationRef);

      if (notificationDoc.exists()) {
        const notificationData = notificationDoc.data();

        // Create an update object that only changes the current user's read status
        const readByUpdate = {
          [`read_by.${userId}`]: true,
        };

        await updateDoc(notificationRef, readByUpdate);
      }
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const markEmergencyAsRead = async () => {
    if (!emergencyNotification || !auth.currentUser) return;

    try {
      await markNotificationAsRead(emergencyNotification.id);
      setShowEmergencyPopup(false);
    } catch (error) {
      console.error("Error marking emergency as read:", error);
    }
  };

  return {
    notifications,
    unreadNotifications,
    emergencyNotification,
    // showEmergencyPopup,
    markEmergencyAsRead,
    markNotificationAsRead,
  };
};
